<script setup lang="ts">
/**
 * Select con autocomplete per lookup indirizzi (country, region, province, city, zipcode).
 * options: { id: number; name?: string; label?: string }[]
 * Mostra name o label; emette l'id selezionato.
 */
const props = withDefaults(
	defineProps<{
		modelValue: number | null;
		options: { id: number; name?: string; label?: string }[];
		placeholder?: string;
		disabled?: boolean;
		loading?: boolean;
	}>(),
	{ placeholder: 'Cerca...', disabled: false, loading: false }
);

const emit = defineEmits<{
	'update:modelValue': [value: number | null];
	advance: [];
}>();

const triggerRef = ref<HTMLButtonElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const isOpen = ref(false);
const search = ref('');
const highlightedIndex = ref(0);

const selectedLabel = computed(() => {
	if (props.modelValue == null) return '';
	const o = props.options.find((x) => x.id === props.modelValue);
	return o ? (o.label ?? o.name ?? '') : '';
});

const filteredOptions = computed(() => {
	const q = search.value.trim().toLowerCase();
	if (!q) return props.options;
	return props.options.filter((o) => {
		const text = (o.label ?? o.name ?? '').toLowerCase();
		return text.includes(q);
	});
});

watch(highlightedIndex, (idx) => {
	const max = Math.max(0, filteredOptions.value.length - 1);
	if (idx < 0 || idx > max) highlightedIndex.value = Math.max(0, Math.min(idx, max));
});
watch(filteredOptions, (opts) => {
	const max = Math.max(0, opts.length - 1);
	if (highlightedIndex.value > max) highlightedIndex.value = max;
});
const highlightedElRef = ref<HTMLElement | null>(null);
function setHighlightedEl(el: unknown) {
	highlightedElRef.value = el ? (el as HTMLElement) : null;
}
watch(highlightedIndex, () => {
	nextTick(() => highlightedElRef.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
});

function open() {
	if (props.disabled) return;
	isOpen.value = true;
	search.value = '';
	const opts = filteredOptions.value;
	const currentIdx = props.modelValue != null ? opts.findIndex((o) => o.id === props.modelValue) : -1;
	highlightedIndex.value = currentIdx >= 0 ? currentIdx : 0;
	nextTick(() => inputRef.value?.focus());
}

function close() {
	isOpen.value = false;
	search.value = '';
}

function select(opt: { id: number; name?: string; label?: string }, advance = false) {
	emit('update:modelValue', opt.id);
	close();
	if (advance) nextTick(() => emit('advance'));
}

function onInputBlur() {
	setTimeout(close, 200);
}

function onKeydown(e: KeyboardEvent) {
	if (e.key === 'Escape') {
		close();
		return;
	}
	if (e.key === 'ArrowDown') {
		e.preventDefault();
		const opts = filteredOptions.value;
		if (opts.length) highlightedIndex.value = Math.min(highlightedIndex.value + 1, opts.length - 1);
		return;
	}
	if (e.key === 'ArrowUp') {
		e.preventDefault();
		highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
		return;
	}
	if (e.key === 'Enter') {
		e.preventDefault();
		const opts = filteredOptions.value;
		if (opts.length) {
			const opt = opts[highlightedIndex.value];
			if (opt) select(opt, true);
		}
		return;
	}
}

function focus() {
	triggerRef.value?.focus();
}

defineExpose({ focus });
</script>

<template>
	<div class="relative">
		<button
			ref="triggerRef"
			type="button"
			:disabled="disabled"
			class="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm focus:border-[#A21B1F] focus:outline-none focus:ring-1 focus:ring-[#A21B1F] disabled:bg-gray-100 disabled:text-gray-500"
			@click="open"
		>
			<span :class="{ 'text-gray-500': !selectedLabel }">
				{{ selectedLabel || placeholder }}
			</span>
			<span v-if="loading" class="material-symbols-outlined animate-spin text-lg text-gray-400">progress_activity</span>
			<span v-else class="material-symbols-outlined text-lg text-gray-400">expand_more</span>
		</button>
		<div
			v-if="isOpen"
			class="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
		>
			<div class="sticky top-0 border-b border-gray-100 bg-white px-2 pb-1 pt-1">
				<input
					ref="inputRef"
					v-model="search"
					type="text"
					class="w-full rounded border border-gray-200 py-1.5 px-2 text-sm focus:border-[#A21B1F] focus:outline-none focus:ring-1 focus:ring-[#A21B1F]"
					placeholder="Digita per filtrare..."
					@blur="onInputBlur"
					@keydown="onKeydown"
				>
			</div>
			<ul class="py-1" role="listbox">
				<li
					v-for="(opt, index) in filteredOptions"
					:key="opt.id"
					:ref="index === highlightedIndex ? setHighlightedEl : undefined"
					role="option"
					:aria-selected="index === highlightedIndex"
					class="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
					:class="{
						'bg-[#A21B1F]/10 text-[#A21B1F]': modelValue === opt.id,
						'bg-gray-100': index === highlightedIndex && modelValue !== opt.id,
					}"
					@mousedown.prevent="select(opt, false)"
				>
					{{ opt.label ?? opt.name ?? opt.id }}
				</li>
				<li v-if="!filteredOptions.length" class="px-3 py-2 text-sm text-gray-500">
					Nessun risultato
				</li>
			</ul>
		</div>
	</div>
</template>
